/* eslint-disable import/order */
const uuidv4 = require('uuid/v4');

const ApiService = require('../../../../service/api');

// Redux
const {
  STEP_NEXT,
  REQUEST_STARTED,
  SAVE_CAPTCHA_TOKEN,
  REQUEST_FINISHED,
  STEP_LOGOUT,
  SNACKBAR_TRIGGER,
  LOADER_PAY_BUTTON,
  LOADER_TRIGGERED,
} = require('../../../../spa/actions/types');

// Constants
const {
  PAGE_ACTIONS,
  CAPTCHA_VERSION,
  CHECKOUT_TYPE,
  URLS: { BASE_URL_MERCADO_LIBRE },
  PLATFORM,
} = require('../../../../../constants/app');
const { INPUTS_ID } = require('../../../../../constants/ui');

// Utils
const { getQueryParams } = require('../../../../utils/Dom');
const { DISCOUNT_CODE_VALIDATIONS, validate } = require('../../../../utils/regex');

export const useActions = ({
  flow,
  siteId,
  browserName,
  history,
  stepActions,
  snackbarActions,
  captchaToken,
  captchaConfig,
  discountCode,
  setDiscountCodeErrorMessage,
  payer,
  email,
  emailErrorMessage,
  setEmailErrorMessage,
  challengesInstructions,
  captcha,
  translations,
  useAlternativePaymentMethod,
  loadingActions,
  requestActions,
  platform,
  configurations
}) => {
  const requestId = uuidv4();

  // ===== Utilities =====

  const generateTokenCaptchaV3 = () => {
    requestActions[REQUEST_STARTED]({ requestId });
    const promiseToken = new Promise((resolve, reject) => {
      try {
        return window.grecaptcha.enterprise
          .execute(captchaConfig.siteKeyV3, { action: captchaConfig.action })
          .then((token) => {
            requestActions[REQUEST_FINISHED]({
              requestId,
              leaveLoading: true,
            });
            if (captchaToken) {
              captchaToken[SAVE_CAPTCHA_TOKEN](token);
            }
            resolve(token);
          });
      } catch (error) {
        requestActions[REQUEST_FINISHED]({
          requestId,
          leaveLoading: true,
        });
        reject(error);
      }
    });
    return promiseToken;
  };

  const showNotification = ({ status = 'default', component, displayTimeout = 10000 }) => {
    // set action data for error message
    const snackbarActionData = {
      show: true,
      status,
      message: component,
    };

    snackbarActions[SNACKBAR_TRIGGER](snackbarActionData);
    setTimeout(() => {
      snackbarActionData.show = false;
      snackbarActions[SNACKBAR_TRIGGER](snackbarActionData);
    }, displayTimeout);
  };

  // ===== GENERAL ACTIONS =====

  const nextStepAction = ({ data }) => {
    const queryParams = getQueryParams();

    if (typeof useAlternativePaymentMethod === 'boolean') {
      data.values.alternative_payment_method = useAlternativePaymentMethod;
    }

    stepActions[STEP_NEXT](
      data,
      flow.id,
      {
        type: flow.type,
        urlParams: queryParams,
      },
      flow.type,
      queryParams,
      history,
    );
  };

  const goToNextStep = async (data) => {
    if (captcha?.version === CAPTCHA_VERSION.V3) {
      if (window.captchaReady) {
        await generateTokenCaptchaV3();
      } else if (!window.captchaError) {
        // In case Captcha from google is still loading we will save in window the callback function to execute when ready
        window.executeAction = async () => {
          await generateTokenCaptchaV3();
          nextStepAction({ data });
        };
        // If Google's Api is still loading should save a callback method to continue with normal path if Google's Api fail to load
        window.executeErrorAction = () => {
          nextStepAction({ data });
        };
        return;
      }
    }

    nextStepAction({ data });
  };

  // ===== STEP ACTIONS =====

  const changePaymentMethod = () => {
    const data = {
      id: PAGE_ACTIONS.CHANGE_PAYMENT_OPTION,
      values: {},
    };
    goToNextStep(data);
  };

  const choosePaymentMethods = () => {
    const data = {
      id: PAGE_ACTIONS.NEXT_STEP,
      values: {},
    };
    goToNextStep(data);
  };

  const changeInstallments = () => {
    const data = {
      id: PAGE_ACTIONS.CHANGE_INSTALLMENTS,
      values: {},
    };
    goToNextStep(data);
  };

  const dismissConsumerCredits = () => {
    const data = {
      id: PAGE_ACTIONS.DISMISS_CONSUMER_CREDITS,
      values: {},
    };
    goToNextStep(data);
  };

  const payConsumerCredits = () => {
    const data = {
      id: PAGE_ACTIONS.PAY_WITH_CONSUMER_CREDITS,
      values: {},
    };
    goToNextStep(data);
  };

  const sentDiscountCode = () => {
    // Validate the discount code
    const validation = validate(discountCode, DISCOUNT_CODE_VALIDATIONS(translations));
    if (validation.error) {
      setDiscountCodeErrorMessage(validation.message);

      // Accessibility - Set focus to the discount code input
      document.getElementById(INPUTS_ID.DISCOUNT_CODE)?.focus();

      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });

      return;
    }

    const data = {
      id: PAGE_ACTIONS.SEND_DISCOUNT_CODE,
      values: {
        discount: {
          discount_code: discountCode,
        },
      },
    };

    goToNextStep(data);
  };

  const changeExtraData = () => {
    if (!payer?.extra_data?.is_modifiable) {
      return;
    }

    // To Define the Step Action
    const data = {
      id: PAGE_ACTIONS.CHANGE_EXTRA_DATA,
      values: {},
    };
    goToNextStep(data);
  };

  const kyc = () => {
    const data = {
      id: PAGE_ACTIONS.PAY,
      values: {
        payer: {
          email,
        },
      },
    };
    goToNextStep(data);
  };

  const reviewExpressKyC = (payload) => {
    const data = {
      id: PAGE_ACTIONS.REVIEW_EXPRESS_PAY,
      values: payload,
    };
    goToNextStep(data);
  }

  const pay = () => {
    // KyC validation
    if (challengesInstructions?.kyc && Object.keys(challengesInstructions?.kyc).length) {
      return;
    }

    // Email validation
    if (payer.is_guest) {
      if (!email || email?.length === 0) {
        setEmailErrorMessage?.(translations.COMPLETE_THIS_DATA);

        // Accessibility - Set focus to the email input
        document.getElementById(INPUTS_ID.USER_EMAIL)?.focus();
        return;
      }

      if (emailErrorMessage?.length > 0) {
        return;
      }
    }

    // Show Loader Wrapper animation
    loadingActions[LOADER_TRIGGERED]('empty', 'pay');
    // Start Pay Button Loading Animation
    loadingActions[LOADER_PAY_BUTTON](true);

    // New Response
    const data = {
      id: PAGE_ACTIONS.PAY,
      values: {
        payer: {
          email,
        },
      },
    };
    goToNextStep(data);
  };

  const reviewExpressPay = (payload) => {
    // KyC validation
    if (challengesInstructions?.kyc && Object.keys(challengesInstructions?.kyc).length) {
      return;
    }

    // Show Loader Wrapper animation
    loadingActions[LOADER_TRIGGERED]('empty', 'pay');

    // Start Pay Button Loading Animation
    loadingActions[LOADER_PAY_BUTTON](true);

    // New Response
    const data = {
      id: PAGE_ACTIONS.REVIEW_EXPRESS_PAY,
      values: payload,
    };

    goToNextStep(data);
  };

  const changeShippingOption = () => {
    const data = {
      id: PAGE_ACTIONS.CHANGE_SHIPPING_OPTION,
      values: {},
    };
    goToNextStep(data);
  };

  const changeShippingAddress = () => {
    const data = {
      id: PAGE_ACTIONS.CHANGE_SHIPPING_ADDRESS,
      values: {},
    };
    goToNextStep(data);
  };

  const changeEmail = () => {
    const data = {
      id: PAGE_ACTIONS.CHANGE_EMAIL,
      values: {},
    };
    goToNextStep(data);
  };

  return {
    pay,
    kyc,
    changeEmail,
    choosePaymentMethods,
    changeExtraData,
    showNotification,
    sentDiscountCode,
    reviewExpressKyC,
    reviewExpressPay,
    payConsumerCredits,
    changeInstallments,
    changePaymentMethod,
    changeShippingOption,
    changeShippingAddress,
    dismissConsumerCredits,
  };
};
